/**
 * SubNav definitions per focus scope.
 *
 * Each scope has an ordered list of tabs; the first tab is always
 * "dashboard". Tab content components live in `pages/workloads/{scope}/`.
 *
 * Per-pool SLO design note:
 *
 *   Schedules used to live in their own tab on both Service and Pool;
 *   they have moved into the new Autoscaling tab as a "Schedules"
 *   section, because operators reason about them as one of several
 *   inputs to the autoscaler ("the SLO loop runs continuously, then
 *   schedules pin a min/max on top, then a manual pin can override
 *   either"). Keeping them in a separate top-level tab obscured that
 *   precedence.
 */
import type { LucideIcon } from "lucide-react";
import {
  Box,
  CircleDollarSign,
  Cloud,
  Container,
  Cpu,
  Gauge,
  GitBranch,
  Info,
  LayoutDashboard,
  LineChart,
  ListChecks,
  Microchip,
  Network,
  PiggyBank,
  Rocket,
  Settings,
  Terminal,
} from "lucide-react";

import type { FocusType } from "./focus";

export interface SubNavTab {
  id: string;
  label: string;
  icon: LucideIcon;
}

export const GLOBAL_TABS: SubNavTab[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "cost", label: "Cost", icon: CircleDollarSign },
  { id: "capacity", label: "Capacity", icon: Gauge },
  { id: "bank", label: "Bank", icon: PiggyBank },
  { id: "rollouts", label: "Rollouts", icon: Rocket },
  { id: "events", label: "Events", icon: ListChecks },
  { id: "providers", label: "Providers", icon: Cloud },
  { id: "container-registries", label: "Registries", icon: Container },
  { id: "egress-ips", label: "Egress IPs", icon: Network },
  { id: "settings", label: "Settings", icon: Settings },
];

// Pool list lives in the left tree, so the "Pools" tab on service is
// redundant. "Workload" is the place to define what to deploy
// (docker image / vLLM + HF model / ...). "Providers" lets the
// operator scope which clouds this service may use and override
// provider credentials. "Autoscaling" replaces "Schedules" — the
// SLO sets, dynamics defaults, and per-window schedules all live
// inside it.
//
// "Events" is a per-scope view of the global event stream filtered
// to this service. It used to be a small "events" link in the
// detail header (top-right) which felt out of place — every other
// way to inspect a service was a left-side tab. Surfacing the
// stream as a dedicated tab puts the operator's "what just
// happened on this service?" entry point in the same column as
// every other concern.
export const SERVICE_TABS: SubNavTab[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "workload", label: "Workload", icon: Box },
  { id: "routing", label: "Routing", icon: GitBranch },
  { id: "autoscaling", label: "Autoscaling", icon: Gauge },
  { id: "insights", label: "Insights", icon: LineChart },
  { id: "cost", label: "Cost", icon: CircleDollarSign },
  { id: "providers", label: "Providers", icon: Cloud },
  { id: "events", label: "Events", icon: ListChecks },
  { id: "settings", label: "Settings", icon: Settings },
];

// Spec carries the resource shape and "where it runs" knobs that used
// to live on Settings — the most operator-faced surface of a pool, so
// it gets its own tab right after Dashboard. Workload sits between
// Spec and Replicas: a pool either *inherits* the service-level
// workload verbatim (default) or *owns* its workload (TTS-style:
// bf16 model on H100/A100, int4 on L4) — the toggle lives on this
// dedicated tab so the per-pool draft + rollout cycle is discoverable.
// Settings is reduced to the Danger zone (pool detach) since
// everything actionable now has a dedicated home (Spec / Workload /
// Autoscaling / Providers).
//
// "Events" mirrors the per-service Events tab: pool-scoped slice of
// the global event stream so the operator stays in this pool's
// SubNav while inspecting "what just happened to this pool".
//
// "Logs" is for the SkyServe controller / load-balancer raw log
// (it lives on the SkyServe controller VM, separate from this
// pool's pods). It used to be a toggle inside the Replicas tab
// header which made it both hard to find and easy to confuse with
// pod-level logs; promoting it to its own tab makes the
// distinction explicit (events stream vs. raw controller log).
export const POOL_TABS: SubNavTab[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "spec", label: "Spec", icon: Microchip },
  { id: "workload", label: "Workload", icon: Box },
  { id: "replicas", label: "Replicas", icon: Cpu },
  { id: "autoscaling", label: "Autoscaling", icon: Gauge },
  { id: "cost", label: "Cost", icon: CircleDollarSign },
  { id: "providers", label: "Providers", icon: Cloud },
  { id: "events", label: "Events", icon: ListChecks },
  { id: "logs", label: "Logs", icon: Terminal },
  { id: "settings", label: "Settings", icon: Settings },
];

// "Events" on a pod is filtered to this pod's replica (events are
// tagged at replica granularity; pods of the same replica share a
// stream). It replaces the events link that previously lived in
// the pod detail header.
export const POD_TABS: SubNavTab[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "metrics", label: "Metrics", icon: LineChart },
  { id: "info", label: "Info", icon: Info },
  { id: "events", label: "Events", icon: ListChecks },
];

export function tabsFor(type: FocusType): SubNavTab[] {
  switch (type) {
    case "global":
      return GLOBAL_TABS;
    case "service":
      return SERVICE_TABS;
    case "pool":
      return POOL_TABS;
    case "pod":
      return POD_TABS;
  }
}

export function defaultTab(type: FocusType): string {
  return tabsFor(type)[0].id;
}

export function isValidTab(type: FocusType, tab: string): boolean {
  return tabsFor(type).some((t) => t.id === tab);
}
